/* ===========================================================================
 * THE ONE FOCUS TREATMENT.
 *
 * Every interactive thing in the system shows keyboard focus the same way, and
 * it is defined here rather than restated per component. Before this there were
 * THREE dialects:
 *
 *   Button / IconButton / Checkbox / Switch
 *       ring-2 ring-ring ring-offset-2 ring-offset-background
 *   FilterChip
 *       ring-2 ring-ring ring-offset-2 ring-offset-surface     <- different gap
 *   Input / Select / Combobox / Textarea  (via control.ts)
 *       border-primary + ring-[3px] ring-primary/25            <- 3px, 25%, no gap
 *
 * So a field's focus ring was a different WIDTH, a different COLOUR and a
 * different SHAPE from the button beside it in the same form.
 *
 * WHY `outline` AND NOT `ring`. Tailwind's `ring-offset-*` paints the gap in an
 * OPAQUE named colour, which forces every control to declare what it is sitting
 * on. That is a guess each component gets wrong half the time: a Button assumes
 * `background` (the page) but most buttons in this app sit on a Card, so the
 * gap painted the PAGE colour across the card — measured in dark as a
 * rgb(20 18 28) band on a rgb(32 30 41) surface. FilterChip assumed `surface`
 * and had the mirror-image bug on the page.
 *
 * `outline-offset` leaves the gap TRANSPARENT, so it shows whatever ground the
 * control actually has. One rule, correct on a card, on the page, on a coloured
 * strip and on map tiles, with nothing to keep in sync.
 *
 * 2px at 2px offset is kept from the button dialect — it is the one that
 * already cleared 3:1 against every surface in the audit, and WCAG 2.4.13 wants
 * a focus indicator at least 2px thick.
 * ======================================================================== */
export const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/**
 * The same treatment for a control whose real `<input>` is a transparent
 * overlay and whose visible body is a SIBLING — `Switch`, and anything else
 * built with Tailwind's `peer`. The outline has to land on the painted element,
 * not on the invisible one that actually holds focus.
 */
/**
 * The INSET variant, for a control that is flush with the edge of the thing
 * that clips it — a sort button filling its `<th>` inside a table with
 * `overflow-hidden`. An outset ring on those is drawn outside the clip and
 * simply vanishes, so the negative offset pulls it inside the control instead.
 * Same width and colour; only the direction changes.
 */
export const insetFocusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';

export const peerFocusRing =
  'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring';

/**
 * The one disabled treatment. Buttons and marks faded to 50% while fields faded
 * to 70% — a disabled field looked more available than a disabled button in the
 * same form. 50% everywhere.
 *
 * `pointer-events-none` is deliberately NOT here: a form control keeps its
 * pointer events so the native `disabled` cursor still shows, while a button
 * drops them so `.state-layer` cannot light up under the mouse. That difference
 * is behavioural, so each component states it.
 */
export const disabledFade = 'disabled:opacity-50';
